/**
 * AccountsTable — portfolio ledger of accounts (health, active projects,
 * open RAID). Extracted from PortfolioDashboardPage.tsx (M2 T3) so the M2
 * canvas's ACCOUNTS_TABLE builtin widget (AccountsTableHost, T5) and the
 * page can both import one module. Root wrapper is grid-fill-ready
 * (height:100%, flex column, internal vertical scroll) but renders
 * identically to before when embedded in normal (unconstrained) page flow.
 */
import { useNavigate } from 'react-router';
import { sectionCardStyle } from './chartTokens';
import { RagBadge } from '@/components/RagBadge';
import type { AccountDashboardEntry } from '@/types/domain';

export interface AccountsTableProps {
  accounts: AccountDashboardEntry[];
}

export function AccountsTable({ accounts }: AccountsTableProps) {
  const navigate = useNavigate();

  if (accounts.length === 0) {
    return (
      <p
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          color: 'var(--text-low)',
          padding: '24px 0',
          margin: 0,
        }}
      >
        No accounts in the portfolio yet.
      </p>
    );
  }

  return (
    <div style={sectionCardStyle({ padding: 0, overflow: 'hidden', maxHeight: '100%', display: 'flex', flexDirection: 'column' })}>
      <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'auto' }}>
        <table className="ledger">
          <thead>
            <tr>
              <th>Account</th>
              <th>Health</th>
              <th style={{ textAlign: 'center' }}>Projects</th>
              <th style={{ textAlign: 'center' }}>Open RAID</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((acct, rowIndex) => (
              <tr
                key={acct.accountId}
                className="row-in hover-lift"
                style={{ cursor: 'pointer', ['--i' as unknown as string]: Math.min(rowIndex, 14) }}
                onClick={() => navigate(`/account/${acct.accountId}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/account/${acct.accountId}`);
                  }
                }}
                tabIndex={0}
                aria-label={`Open account ${acct.accountName}`}
              >
                <td>
                  <span style={{ fontWeight: 600, color: 'var(--text-hi)' }}>
                    {acct.accountName}
                  </span>
                </td>
                <td>
                  <RagBadge rag={acct.worstHealth} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-hi)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {acct.activeProjectCount}
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--text-sm)',
                      color: acct.openRaidCount > 0 ? 'var(--health-amber)' : 'var(--text-mid)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {acct.openRaidCount}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
